import React, { useState, useMemo } from 'react';
import { sanitizeUrl } from '@braintree/sanitize-url';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Plus, Minus, Search, ExternalLink, Server, Globe, MapPin, LayoutGrid, Table as TableIcon } from 'lucide-react';
import { useWebsites } from '@/hooks/useWebsites';
import { useHostings } from '@/hooks/useHostings';
import { useWebsiteHostingMap } from '@/hooks/useWebsiteHostingMap';
// import { useUptimeKuma } from '@/hooks/useUptimeKuma';
import HostingGroupsView from '@/components/HostingGroupsView';

interface SitesMapTableProps {
  className?: string;
}

const SitesMapTable: React.FC<SitesMapTableProps> = ({ className }) => {
  const { websites, loading: websitesLoading } = useWebsites();
  const { hostings, loading: hostingsLoading } = useHostings();
  const { mappings, createMapping, deleteMapping, getMappingsForWebsite } = useWebsiteHostingMap();
  // const { onlineCount, offlineCount, offlineSites, isLoading: uptimeLoading, errorMessage, getMonitorsStatus, getMonitorStatus } = useUptimeKuma();

  // State for view mode
  const [viewMode, setViewMode] = useState<'table' | 'groups'>('groups');

  // State for filters
  const [searchTerm, setSearchTerm] = useState('');
  const [hostingFilter, setHostingFilter] = useState('all');

  // State for assignment dialog
  const [selectedWebsite, setSelectedWebsite] = useState<string | null>(null);
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
  const [selectedHostingId, setSelectedHostingId] = useState('');

  const getSafeUrl = (rawUrl: string) => {
    const sanitized = sanitizeUrl(rawUrl);
    return sanitized === 'about:blank' ? null : sanitized;
  };

  // Filtered websites
  const filteredWebsites = useMemo(() => {
    return websites.filter(website => {
      const matchesSearch = website.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          website.url.toLowerCase().includes(searchTerm.toLowerCase());

      if (hostingFilter === 'all') return matchesSearch;

      if (hostingFilter === 'unassigned') {
        return matchesSearch && getMappingsForWebsite(website.id).length === 0;
      }

      // Check if website has specific hosting assigned
      const websiteMappings = getMappingsForWebsite(website.id);
      return matchesSearch && websiteMappings.some(mapping => mapping.hosting_id === hostingFilter);
    });
  }, [websites, searchTerm, hostingFilter, getMappingsForWebsite]);

  // Get assigned hosting names for a website
  const getAssignedHostingNames = (websiteId: string) => {
    const websiteMappings = getMappingsForWebsite(websiteId);
    return websiteMappings.map(mapping => mapping.hosting_name).filter(Boolean);
  };

  // Handle assignment
  const handleAssignHosting = async () => {
    if (!selectedWebsite || !selectedHostingId) return;

    const success = await createMapping({
      website_id: selectedWebsite,
      hosting_id: selectedHostingId,
    });

    if (success) {
      setIsAssignDialogOpen(false);
      setSelectedWebsite(null);
      setSelectedHostingId('');
    }
  };

  // Handle unassignment
  const handleUnassignHosting = async (mappingId: string) => {
    await deleteMapping(mappingId);
  };

  const openAssignDialog = (websiteId: string) => {
    setSelectedWebsite(websiteId);
    setIsAssignDialogOpen(true);
  };

  if (websitesLoading || hostingsLoading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle>Mapa stron</CardTitle>
          <CardDescription>Ładowanie danych...</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Mapa stron
            </CardTitle>
            <CardDescription>
              Zarządzaj przypisaniami hostingów do domen
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button
              variant={viewMode === 'groups' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('groups')}
              className="gap-2"
            >
              <LayoutGrid className="h-4 w-4" />
              Widok grupowy
            </Button>
            <Button
              variant={viewMode === 'table' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('table')}
              className="gap-2"
            >
              <TableIcon className="h-4 w-4" />
              Widok tabelaryczny
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mt-4">
          <div className="flex-1 min-w-64">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Szukaj domen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          <Select value={hostingFilter} onValueChange={setHostingFilter}>
            <SelectTrigger className="w-64">
              <SelectValue placeholder="Filtruj po hostingu" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Wszystkie domeny</SelectItem>
              <SelectItem value="unassigned">Bez przypisanego hostingu</SelectItem>
              {hostings.map(hosting => (
                <SelectItem key={hosting.id} value={hosting.id}>
                  {hosting.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>

      <CardContent>
        {viewMode === 'groups' ? (
          <HostingGroupsView
            onAssignClick={openAssignDialog}
            onUnassignClick={handleUnassignHosting}
          />
        ) : (
          <div className="border rounded-lg overflow-hidden">
            <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead>Domena</TableHead>
                <TableHead>URL</TableHead>
                <TableHead>Kategoria</TableHead>
                <TableHead>Przypisane hostingi</TableHead>
                <TableHead className="w-32">Akcje</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredWebsites.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                    {websites.length === 0 ? 'Brak domen. Dodaj pierwszą domenę.' : 'Brak domen spełniających kryteria filtrowania.'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredWebsites.map((website) => {
                  const assignedHostings = getAssignedHostingNames(website.id);
                  const websiteMappings = getMappingsForWebsite(website.id);
                  const safeUrl = getSafeUrl(website.url);

                  return (
                    <TableRow key={website.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <Globe className="h-4 w-4 text-blue-500" />
                          {website.name}
                        </div>
                      </TableCell>
                      <TableCell>
                        {safeUrl ? (
                          <a
                            href={safeUrl || undefined}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 underline flex items-center gap-1"
                          >
                            <ExternalLink className="h-3 w-3" />
                            {website.url}
                          </a>
                        ) : (
                          <span className="text-muted-foreground">{website.url}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {website.category ? (
                          <Badge variant="secondary">{website.category}</Badge>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {assignedHostings.length === 0 ? (
                            <span className="text-muted-foreground">Brak przypisań</span>
                          ) : (
                            assignedHostings.map((hostingName, index) => (
                              <Badge key={index} variant="outline" className="flex items-center gap-1">
                                <Server className="h-3 w-3" />
                                {hostingName}
                              </Badge>
                            ))
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openAssignDialog(website.id)}
                            className="h-8 w-8 p-0"
                            title="Przypisz hosting"
                          >
                            <Plus className="h-3 w-3" />
                          </Button>

                          {/* Show unassign buttons for assigned hostings */}
                          {websiteMappings.map((mapping) => (
                            <AlertDialog key={mapping.id}>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                                  title={`Usuń przypisanie ${mapping.hosting_name}`}
                                >
                                  <Minus className="h-3 w-3" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Usuń przypisanie</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Czy na pewno chcesz usunąć przypisanie hostingu <strong>{mapping.hosting_name}</strong> do domeny <strong>{website.name}</strong>?
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Anuluj</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleUnassignHosting(mapping.id)}
                                    className="bg-red-600 hover:bg-red-700"
                                  >
                                    Usuń przypisanie
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          ))}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
        )}

        {/* Assignment Dialog */}
        <Dialog open={isAssignDialogOpen} onOpenChange={setIsAssignDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Przypisz hosting do domeny</DialogTitle>
              <DialogDescription>
                Wybierz hosting który chcesz przypisać do domeny
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Wybierz hosting</label>
                <Select value={selectedHostingId} onValueChange={setSelectedHostingId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Wybierz hosting z listy" />
                  </SelectTrigger>
                  <SelectContent>
                    {hostings
                      .filter(hosting => {
                        // Filter out already assigned hostings for this website
                        const websiteMappings = getMappingsForWebsite(selectedWebsite || '');
                        return !websiteMappings.some(mapping => mapping.hosting_id === hosting.id);
                      })
                      .map(hosting => (
                        <SelectItem key={hosting.id} value={hosting.id}>
                          {hosting.name} ({hosting.panel_url})
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setIsAssignDialogOpen(false)}>
                Anuluj
              </Button>
              <Button onClick={handleAssignHosting} disabled={!selectedHostingId}>
                Przypisz hosting
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default SitesMapTable;
